import { marked } from "marked";
import TurndownService from "turndown";
import DOMPurify from "dompurify";

// GFM + sauts de ligne simples conservés : les anciens tickets sont du texte
// brut saisi dans un TextField multiligne, ils doivent s'afficher à l'identique.
marked.setOptions({ gfm: true, breaks: true });

/** Markdown → HTML assaini (affichage et chargement dans l'éditeur WYSIWYG). */
export function markdownToHtml(md) {
    if (!md) return "";
    return DOMPurify.sanitize(marked.parse(String(md)), { ADD_ATTR: ["target"] });
}

const turndown = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
});
// Quill n'a pas de balise <del> : le barré arrive en <s>.
turndown.addRule("strike", {
    filter: ["s", "del", "strike"],
    replacement: (content) => `~~${content}~~`,
});
// Quill 2 émet un <br> seul dans les paragraphes vides : on n'en garde rien.
turndown.addRule("emptyParagraph", {
    filter: (node) => node.nodeName === "P" && node.textContent.trim() === "" && !node.querySelector("img"),
    replacement: () => "\n\n",
});

// Une image collée en data-URL gonflerait le ticket de plusieurs Mo : seules
// les images téléversées (URL http) sont conservées.
turndown.addRule("dataImage", {
    filter: (node) => node.nodeName === "IMG" && /^data:/i.test(node.getAttribute("src") || ""),
    replacement: () => "",
});

// Quill 2 sérialise ses blocs de code en <pre> sans <code> interne.
turndown.addRule("quillCodeBlock", {
    filter: (node) => node.nodeName === "PRE" && !node.querySelector("code"),
    replacement: (_content, node) => `\n\n\`\`\`\n${node.textContent.replace(/^\n+|\n+$/g, "")}\n\`\`\`\n\n`,
});
// Puces compactes (« - a ») plutôt que l'indentation par défaut (« -   a »).
turndown.addRule("listItem", {
    filter: "li",
    replacement: (content, node, options) => {
        const parent = node.parentNode;
        let prefix = `${options.bulletListMarker} `;
        if (parent.nodeName === "OL") {
            const start = Number(parent.getAttribute("start") || 1);
            prefix = `${start + Array.prototype.indexOf.call(parent.children, node)}. `;
        }
        const body = content.replace(/^\n+/, "").replace(/\n+$/, "\n")
            .replace(/\n/gm, `\n${" ".repeat(prefix.length)}`);
        return prefix + body + (node.nextSibling && !/\n$/.test(body) ? "\n" : "");
    },
});

/** HTML (sortie Quill) → Markdown, chaîne vide pour un éditeur vide. */
export function htmlToMarkdown(html) {
    if (!html) return "";
    return turndown.turndown(html)
        // getSemanticHTML remplace les espaces par des insécables.
        .replace(/ /g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/** Texte brut d'un Markdown, pour les aperçus d'une ligne (cartes, cloche). */
export function markdownToText(md) {
    if (!md) return "";
    const div = document.createElement("div");
    div.innerHTML = markdownToHtml(md);
    div.querySelectorAll("img").forEach(img => img.replaceWith("🖼 "));
    return (div.textContent || "").replace(/\s+/g, " ").trim();
}
